import { Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { distinctUntilChanged, filter, first, map, pairwise, startWith, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Attivazione, AttivazioneConsents, AttivazioneFeedback, Status } from './../../models/attivazione';

import 'moment/locale/it';

import { ModalDialogService } from 'ngx-modal-dialog';
import { AlgConditionModalComponent } from '../../components/dialogs/alg-condition-modal/alg-condition-modal.component';
import { UtilsService } from '../../services/utils.service';

import { environment } from '../../../environments/environment';
import {
  AlgAttivazioniService,
  APPROVAL_STATUS_COMPLETED,
  APPROVAL_STATUS_ERROR,
  APPROVAL_STATUS_INVALID,
  APPROVAL_STATUS_OK,
  APPROVAL_STATUS_SIGNED,
} from '../../services/alg-attivazioni.service';

@Component({
  selector: 'alg-attivazioni',
  templateUrl: './alg-attivazioni.component.html',
  styleUrls: ['./alg-attivazioni.component.scss'],
})
export class AlgAttivazioniComponent implements OnInit, OnDestroy {
  public isOpportunityLoading$: Observable<boolean>;
  public isAttivazioneOk$: Observable<boolean>;
  public isAttivazioneCompleted$: Observable<boolean>;
  public isErrorAlreadySigned$: Observable<boolean>;
  public isErrorInvalidOffer$: Observable<boolean>;
  public isErrorGeneric$: Observable<boolean>;
  public productType$: Observable<string>;
  public operatorRating$: Observable<AttivazioneFeedback>;
  public isConsentsOk: boolean;
  public feedbackOptions = [
    { image: 'bad', value: 100 },
    { image: 'normal', value: 200 },
    { image: 'good', value: 300 },
    { image: 'excellent', value: 400 },
  ];
  public feedbackModalContent = {
    title: 'La tua privacy per noi è importante.',
    description: '',
  };
  private opportunityId$: Observable<any>;
  private opportunityId: string;
  private privacyLinkUrl: string;
  private attivazioneId: string;
  private consents: AttivazioneConsents;
  private ngUnsubscribe = new Subject<void>();
  public isLoading: Observable<boolean>;
  public attError: Observable<any>;
  public attivazione = {} as Attivazione;
  public isLinear: boolean = false;
  public needsPreConsent: boolean = true;
  public isNewConnection: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private utils: UtilsService,
    private modalDialogService: ModalDialogService,
    private viewContainer: ViewContainerRef,
    private api: AlgAttivazioniService
  ) {
    this.opportunityId$ = this.route.params.pipe(map((params) => params && params.id));

    this.isLoading = this.api.isLoading;
    this.attError = this.api.attError.asObservable();

    this.isOpportunityLoading$ = this.api.isOpportunityLoading$;
    this.isAttivazioneOk$ = this.api.attivazioneStatus$.pipe(
      map((status) => status === APPROVAL_STATUS_OK),
      startWith(false),
      distinctUntilChanged()
    );
    this.isAttivazioneCompleted$ = this.api.attivazioneStatus$.pipe(
      map((status) => status === APPROVAL_STATUS_COMPLETED),
      startWith(false),
      distinctUntilChanged()
    );
    this.isErrorAlreadySigned$ = this.api.attivazioneStatus$.pipe(
      map((status) => status === APPROVAL_STATUS_SIGNED),
      startWith(false),
      distinctUntilChanged()
    );
    this.isErrorInvalidOffer$ = this.api.attivazioneStatus$.pipe(
      map((status) => status === APPROVAL_STATUS_INVALID),
      startWith(false),
      distinctUntilChanged()
    );
    this.isErrorGeneric$ = this.api.attivazioneStatus$.pipe(
      map((status) => status === APPROVAL_STATUS_ERROR),
      startWith(false),
      distinctUntilChanged()
    );
    this.productType$ = this.api.attivazioneProduct$.pipe(map((product) => !!product && product.type));
    this.operatorRating$ = this.api.operatorRatingCode$.pipe(first((rating) => !!rating));
  }

  ngOnInit() {
    //check if opportunity id is not empty and is set
    this.opportunityId$
      .pipe(
        filter((id) => typeof id !== 'string' || (typeof id === 'string' && id.trim() === '')),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((id) => {
        window.location.href = environment.websiteUrl;
      });

    //get opportunity from backend
    this.opportunityId$
      .pipe(
        filter((id) => typeof id === 'string' && id.trim() !== ''),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((id) => {
        // const initialAct: Attivazione = {} as Attivazione;
        // initialAct.webStatus = Status.step1;
        // this.api.saveAttivazione(initialAct, false);

        // first request from sf opportunity
        this.api.fetchOpportunity(id);
      });

    // SUBSCRIBE: Keep instance copy of opportunity and attivazione ids
    this.api.attivazione$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((attivazione) => {
      if (!attivazione) return;
      this.attivazione = attivazione;
      this.api.setCurrentStep(attivazione.webStatus);
      this.attivazioneId = !!attivazione && attivazione.id;
      this.opportunityId = !!attivazione && attivazione.opportunityId;
      if (this.attivazione.linearGas || this.attivazione.linearLuce) {
        this.isLinear = true;
      }
      if (this.isNewConnection) {
        this.needsPreConsent = this.attivazione.Alg_OTPCheck__c === false;
        this.isNewConnection = false;
      } else {
        this.needsPreConsent = this.attivazione.Alg_OTPCheck__c === false && this.attivazione.webStatus === Status.step1;
      }
    });

    // SUBSCRIBE: Keep instance copy of consent validity status and consents object
    this.api.isConsentsOk$
      .pipe(withLatestFrom(this.api.privacyConsets$, this.api.genericConsets$), takeUntil(this.ngUnsubscribe))
      .subscribe(([isconsentsOk, privacyConsents, genericConsents]) => {
        this.isConsentsOk = isconsentsOk;
        this.consents = {
          ...privacyConsents,
          ...genericConsents,
          notorietaRadio: this.attivazione.notorietaRadio,
        };
      });
    // SUBSCRIBE: save and prepare policies links
    this.api.policiesUrl$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((policiesUrls) => {
      this.feedbackModalContent = {
        ...this.feedbackModalContent,
        description: `Effettuando la valutazione della chiamata esprimi il consenso al trattamento dei tuoi dati ai fini della valutazione della qualità del servizio. <a href="${policiesUrls.generalPolicy}" target="_blank">Info privacy</a>`,
      };
    });
    // SUBSCRIBE: scroll to top when attivazione status goes to APPROVAL_STATUS_COMPLETED
    this.isAttivazioneCompleted$
      .pipe(
        pairwise(),
        takeUntil(this.ngUnsubscribe),
        filter(([oldStatus, newStatus]) => !oldStatus && newStatus === true)
      )
      .subscribe(() => {
        if (window.scrollTo) {
          window.scrollTo(0, 0);
        }
      });
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public openModal(modalContent) {
    this.utils.disableOverflow();
    this.modalDialogService.openDialog(this.viewContainer, {
      title: modalContent.title,
      childComponent: AlgConditionModalComponent,
      settings: {
        closeButtonClass: 'close theme-icon-close',
        headerTitleClass: ' modal-title modal-title-underline',
      },
      data: {
        description: modalContent.description,
      },
    });
  }

  public readCondition(type) {
    let modalContent;
    if (type === 'dataConsent') {
      modalContent = this.feedbackModalContent;
    }
    this.openModal(modalContent || {});
  }

  // feedback
  public onFeebackModal(event) {
    if (event === 'open') {
      this.readCondition('dataConsent');
    }
  }

  // TODO: Rename this method
  public onFeedback(feedback) {
    // this.api.saveFeedback(
    //   this.attivazioneId,
    //   feedback
    // );
  }

  public nextStep() {
    if (this.isConsentsOk) {
      this.consents.notorietaRadio = this.attivazione.notorietaRadio;
      this.api.saveConsents(this.opportunityId, this.consents, false);
      this.api.nextStep(this.attivazione, Status.step4);
    }
  }

  public handlePreConsentClick() {
    this.needsPreConsent = false;
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/alg-attivazioni/alg-attivazioni.component.ts